var App = App || {};

/* =========================================================================
Comments & Documentation
========================================================================= */

// Example usage:
/*
App.ZagScrollAnimate.initialize('.block-promos', {
    offset: 75,
    targets: [
        { selector: ".rich-text", type: "animate__fadeInRight", stagger: 0 },
        { selector: ".partial-promo", type: "animate__fadeInUp", stagger: 400 }
    ],
    overflow: true
});
*/

// typically the selector used to initalize the plugin is the widget itself
// perhaps if there was significant padding on the widget, an ancestor element may be used

// designers typically don't want the animation kicked off exactly as the element enters the view and waiting until halfway is too long
// kick off the animations when the element is 75% from the top of the screen

// multiple objects within the element can be animated in different ways
// optionally animations from groups of objects can be staggered

// designs for many components lately have elements that are meant to overflow out of the component
// fadeInRight (and maybe others causes a horizontal scroll bar that no one wants to see
// one solution is to optionally set overflow while the animation is occurring

(function () {
    "use strict";

    const defaultOptions = { offset: 50, targets: [], overflow: false };

    /* =========================================================================
    Helpers
    ========================================================================= */

    function validateInitializationOptions(selector, options) {
        let isValid = true;
        if (!selector || typeof selector !== 'string') {
            console.warn('[ZagScrollAnimate] selector is missing or not a string. Initialization skipped.');
            isValid = false;
        }
        if (!options || typeof options !== 'object') {
            console.warn('[ZagScrollAnimate] options is missing or not an object. Initialization skipped.');
            isValid = false;
        }
        if (!Array.isArray(options?.targets)) {
            console.warn('[ZagScrollAnimate] options.targets should be an array. Initialization skipped for:', selector);
            isValid = false;
        }
        if (Array.isArray(options?.targets)) {
            options.targets.forEach((animationTarget, idx) => {
                if (!animationTarget.selector || typeof animationTarget.selector !== 'string') {
                    console.warn(`[ZagScrollAnimate] targets[${idx}].selector is missing or not a string. Skipping this target.`, animationTarget);
                }
                if (!animationTarget.type || typeof animationTarget.type !== 'string') {
                    console.warn(`[ZagScrollAnimate] targets[${idx}].type is missing or not a string. Skipping this target.`, animationTarget);
                }
                if (typeof animationTarget.stagger !== 'number') {
                    console.warn(`[ZagScrollAnimate] targets[${idx}].stagger is missing or not a number. Defaulting to 0.`, animationTarget);
                    animationTarget.stagger = 0;
                }
            });
        }
        return isValid;
    }

    /* =========================================================================
    Initialization
    ========================================================================= */

    function initializeScrollAnimationForContainer(containerElement, options) {
        options.targets.forEach((animationTarget) => {
            if (!animationTarget.selector || typeof animationTarget.selector !== 'string') {
                return;
            }
            if (!animationTarget.type || typeof animationTarget.type !== 'string') {
                return;
            }
            if (typeof animationTarget.stagger !== 'number') {
                animationTarget.stagger = 0;
            }
            let staggerIndex = 0;
            containerElement.querySelectorAll(animationTarget.selector).forEach(function (targetElement) {
                targetElement.setAttribute("data-scroll_animate_type", animationTarget.type);
                targetElement.setAttribute("data-scroll_animate_stagger", staggerIndex * animationTarget.stagger);
                staggerIndex++;
            });
        });

        bindScrollAnimationEvent(containerElement, options);
    }

    /* =========================================================================
    Event Binding
    ========================================================================= */

    function bindScrollAnimationEvent(containerElement, options) {
        // Use IntersectionObserver to trigger animation when element enters viewport
        const observer = new IntersectionObserver((entries, obs) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    triggerScrollAnimations(containerElement, options.overflow);
                    obs.unobserve(entry.target);
                }
            });
        }, {
            threshold: 0,
            rootMargin: `0px 0px -${100 - options.offset}% 0px`
        });
        observer.observe(containerElement);
    }

    /* =========================================================================
    UI Synchronization
    ========================================================================= */

    function triggerScrollAnimations(containerElement, shouldHandleOverflow) {
        if (shouldHandleOverflow) {
            handleOverflowDuringAnimation(containerElement);
        }
        containerElement.querySelectorAll('[data-scroll_animate_type]').forEach(function (targetElement) {
            const animationType = targetElement.getAttribute("data-scroll_animate_type");
            const staggerDelay = parseInt(targetElement.getAttribute("data-scroll_animate_stagger")) || 0;
            setTimeout(function () {
                targetElement.classList.add("animate__animated");
                targetElement.classList.add(animationType);
            }, staggerDelay);
        });
    }

    function handleOverflowDuringAnimation(containerElement) {
        // 1000 ms comes from the animate.css's 1 second animation standard
        const animationDuration = 1000;
        const targetElements = containerElement.querySelectorAll('[data-scroll_animate_type]');
        if (targetElements.length === 0) return;
        const lastTarget = targetElements[targetElements.length - 1];
        const lastStagger = parseInt(lastTarget.getAttribute("data-scroll_animate_stagger")) + animationDuration;
        containerElement.classList.add("zag-scroll-animate-hide-overflow");
        setTimeout(function () {
            containerElement.classList.remove("zag-scroll-animate-hide-overflow");
        }, lastStagger);
    }

    /* =========================================================================
    Public API Exposure
    ========================================================================= */

    App.ZagScrollAnimate = {
        initialize: function (selector, options) {
            if (!validateInitializationOptions(selector, options)) return;
            document.querySelectorAll(selector).forEach(function (containerElement) {
                initializeScrollAnimationForContainer(containerElement, Object.assign({}, defaultOptions, options));
            });
        }
    };
})();
